// Public home page and phone controller: pick a live game → enter PIN and
// nickname → lobby → game. The phone holds no game state of its own. It
// renders whatever view the server sends and sends intents back.

import { NICKNAME_MAX_LENGTH, PIN_LENGTH, PROTOCOL_VERSION, isPin, type GameListing, type RoomView, type ServerMessage } from "../protocol";
import { h, keyed, setField, setList } from "./dom";
import { gameClients } from "./games/registry";
import { api, connectRoom, getDeviceId, showStatus, storageGet, storageSet, type RoomConnection } from "./net";

const app = document.getElementById("app")!;
const statusBar = document.getElementById("status")!;
const toastEl = document.getElementById("toast")!;
const deviceId = getDeviceId();

interface Session {
  gameId: string;
  gameName: string;
  pin: string;
  nickname: string;
}
function loadSession(): Session | null {
  try {
    const s = JSON.parse(storageGet("pg.session") ?? "null");
    return s && typeof s.gameId === "string" && isPin(s.pin) && typeof s.nickname === "string" ? s : null;
  } catch {
    return null;
  }
}
const saveSession = (s: Session | null) => storageSet("pg.session", s && JSON.stringify(s));

let conn: RoomConnection | null = null;
let pollTimer: ReturnType<typeof setInterval> | undefined;
let listing: GameListing[] = [];
let view: HTMLElement;
let playerId = "";

// ---------------------------------------------------------------- branding

const logo = (size: number) => h("img", { src: "/logo.svg", alt: "", width: size, height: size, class: "p-logo" });
const brandName = () => h("span", { class: "p-brand" }, "Game", h("b", {}, "Fundas"));
const iconFor = (gameType: string) => gameClients[gameType]?.icon ?? "🎲";

/** The home page uses a wider two-column layout on laptops; game views stay phone-width. */
function setWide(wide: boolean) {
  app.classList.toggle("wide", wide);
}

// ---------------------------------------------------------------- home: list of live games

function showHome(notice?: string) {
  conn?.close();
  conn = null;
  statusBar.hidden = true;
  setWide(true);
  document.title = "GameFundas · Join a game";
  history.replaceState(null, "", "/");
  const list = h("div", { class: "p-games" }, h("div", { class: "p-empty" }, h("span", { class: "p-spinner" }), "Looking for games…"));
  const resume = h("div");
  app.replaceChildren(
    h(
      "div",
      { class: "p-home" },
      h(
        "header",
        { class: "p-hero" },
        logo(64),
        h("h1", {}, brandName()),
        h("p", { class: "p-tagline" }, "Party games on the big screen. Play from your phone: no app, no sign-up."),
        h(
          "ol",
          { class: "p-steps" },
          h("li", {}, h("span", {}, "📺"), "Find your game below or scan the QR on the TV"),
          h("li", {}, h("span", {}, "🔢"), "Enter the 4-digit PIN from the big screen"),
          h("li", {}, h("span", {}, "📱"), "Pick a nickname and play"),
        ),
      ),
      h(
        "main",
        { class: "p-main" },
        notice ? h("p", { class: "p-notice" }, notice) : null,
        resume,
        h("section", {}, h("div", { class: "p-section-head" }, h("h2", {}, "Live games"), h("span", { class: "p-live" }, "Updates automatically")), list),
      ),
      h("footer", { class: "p-foot" }, "Hosting a game night? ", h("a", { href: "/admin" }, "Game Admin Console →")),
    ),
  );

  const refresh = async () => {
    try {
      listing = await api<GameListing[]>("/api/games");
    } catch {
      return; // keep showing the last list; we'll try again
    }
    const session = loadSession();
    const resumable = session && listing.find((g) => g.id === session.gameId);
    resume.replaceChildren(
      ...(resumable
        ? [
            h(
              "button",
              { class: "p-resume", onclick: () => join(resumable, session.pin, session.nickname) },
              h("span", { class: "p-resume-icon" }, iconFor(resumable.gameType)),
              h("span", { class: "p-resume-text" }, h("small", {}, `Welcome back, ${session.nickname}!`), h("strong", {}, `Resume ${resumable.name}`)),
              h("span", { class: "p-chevron" }, "→"),
            ),
          ]
        : []),
    );
    list.replaceChildren(
      ...(listing.length === 0
        ? [
            h(
              "div",
              { class: "p-empty" },
              h("span", { class: "p-empty-icon" }, "🎲"),
              h("strong", {}, "No games live right now"),
              "When your host starts one, it'll show up here. No need to refresh.",
            ),
          ]
        : listing.map((g) =>
            h(
              "button",
              { class: "p-game", onclick: () => showJoin(g) },
              h("span", { class: "p-game-icon" }, iconFor(g.gameType)),
              h("span", { class: "p-game-text" }, h("strong", {}, g.name), h("small", {}, `${g.gameTitle} · ${playersText(g)}`), statusPill(g)),
              h("span", { class: "p-chevron" }, "→"),
            ),
          )),
    );
  };
  clearInterval(pollTimer);
  pollTimer = setInterval(refresh, 5_000);
  void refresh().then(() => {
    // Arrived via the big screen's QR code: jump straight to that game's PIN form.
    const wanted = new URLSearchParams(location.search).get("game") ?? pendingGameId;
    pendingGameId = null;
    const g = wanted && listing.find((x) => x.id === wanted);
    if (g) showJoin(g);
  });
}
let pendingGameId: string | null = new URLSearchParams(location.search).get("game");

function statusPill(g: GameListing) {
  if (g.status === "lobby") return h("span", { class: "p-pill open" }, "Open to join");
  return h("span", { class: "p-pill live" }, g.lateJoin ? "In progress" : "In progress · rejoin only");
}
const playersText = (g: GameListing) => (g.maxPlayers ? `${g.players}/${g.maxPlayers} players` : `${g.players} player${g.players === 1 ? "" : "s"}`);

// ---------------------------------------------------------------- join form

function showJoin(g: GameListing, error?: string) {
  clearInterval(pollTimer);
  setWide(false);
  document.title = `Join ${g.name} · GameFundas`;
  const session = loadSession();
  const lastNick = session?.nickname ?? storageGet("pg.nickname") ?? "";
  const pinInput = h("input", {
    name: "pin",
    inputmode: "numeric",
    pattern: "[0-9]*",
    maxlength: PIN_LENGTH,
    autocomplete: "off",
    placeholder: "• • • •",
    required: true,
    class: "p-pin",
    "aria-label": "Game PIN",
  });
  const nickInput = h("input", { name: "nickname", value: lastNick, maxlength: NICKNAME_MAX_LENGTH, autocomplete: "nickname", autocapitalize: "words", required: true, placeholder: "e.g. Asha" });
  if (session?.gameId === g.id) pinInput.value = session.pin;
  const locked = g.status !== "lobby" && !g.lateJoin;
  const form = h(
    "form",
    { class: "p-join" },
    h("header", { class: "p-topbar" }, h("button", { type: "button", class: "p-back", onclick: () => showHome(), "aria-label": "All games" }, "←"), logo(26), brandName()),
    h(
      "div",
      { class: "p-card" },
      h("div", { class: "p-card-icon" }, iconFor(g.gameType)),
      h("h1", {}, g.name),
      h("p", { class: "p-sub" }, `${g.gameTitle} · ${playersText(g)}`),
      locked ? h("p", { class: "p-notice" }, "This game has started. If you were playing, rejoin with the same nickname to pick up where you left off.") : null,
      h("label", { class: "p-field" }, h("span", {}, "Game PIN"), pinInput, h("small", {}, "It's on the big screen.")),
      h("label", { class: "p-field" }, h("span", {}, "Your nickname"), nickInput, h("small", {}, "Use the same nickname to rejoin if you drop out.")),
      error && h("p", { class: "p-error" }, error),
      h("button", { type: "submit", class: "p-cta" }, locked ? "Rejoin game →" : "Join game →"),
    ),
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const pin = pinInput.value.trim();
    const nickname = nickInput.value.trim();
    if (!isPin(pin)) return showJoin(g, "The PIN is 4 digits. Check the big screen.");
    if (!nickname) return showJoin(g, "Pick a nickname.");
    join(g, pin, nickname);
  });
  app.replaceChildren(form);
  (pinInput.value ? nickInput : pinInput).focus();
}

// ---------------------------------------------------------------- in a game

function join(g: Pick<GameListing, "id" | "name">, pin: string, nickname: string) {
  clearInterval(pollTimer);
  setWide(false);
  document.title = `${g.name} · GameFundas`;
  history.replaceState(null, "", `/?game=${g.id}`);
  view = h("main", { class: "view" }, h("p", { class: "center muted" }, "Joining…"));
  const nameEl = h("span", {}, nickname);
  app.replaceChildren(
    h("header", { class: "p-gamebar" }, logo(24), h("span", { class: "p-gamebar-name" }, g.name), h("span", { class: "p-me" }, "👤 ", nameEl)),
    view,
  );
  conn = connectRoom({
    gameId: g.id,
    hello: () => ({ t: "hello", v: PROTOCOL_VERSION, role: "player", pin, nickname, deviceId }),
    onStatus: (s) => showStatus(statusBar, s),
    onMessage: (msg) => onMessage(msg, g, pin, nameEl),
  });
}

function onMessage(msg: ServerMessage, g: Pick<GameListing, "id" | "name">, pin: string, nameEl: HTMLElement) {
  switch (msg.t) {
    case "welcome":
      playerId = msg.playerId!;
      nameEl.textContent = msg.nickname!; // server's canonical spelling
      saveSession({ gameId: g.id, gameName: g.name, pin, nickname: msg.nickname! });
      storageSet("pg.nickname", msg.nickname!);
      return;
    case "state":
      return render(msg.room, msg.game);
    case "error":
      switch (msg.code) {
        case "bad_pin":
        case "too_many_attempts":
        case "name_invalid":
        case "name_in_use":
        case "game_started":
        case "room_full": {
          const listed = listing.find((x) => x.id === g.id);
          return listed ? showJoin(listed, msg.message) : showHome(msg.message);
        }
        case "closed":
        case "no_game":
        case "kicked":
          saveSession(null);
          return showHome(msg.message);
        default:
          return msg.fatal ? showHome(msg.message) : toast(msg.message);
      }
  }
}

function render(room: RoomView, game: unknown) {
  const client = gameClients[room.gameType];
  if (room.phase === "lobby") return renderLobby(room, client?.rules);
  if (!client) return void keyed(view, "unknown", () => h("p", { class: "center" }, "This page is out of date. Please reload."));
  client.renderController(view, game, { playerId, room, sendIntent: (intent) => conn?.send({ t: "intent", intent }) });
}

function renderLobby(room: RoomView, rules?: string[]) {
  const me = room.players.find((p) => p.id === playerId)?.nickname ?? "";
  const el = keyed(view, "lobby", () =>
    h(
      "section",
      { class: "p-lobby" },
      h("div", { class: "p-lobby-hero" }, h("div", { class: "p-check" }, "✓"), h("h2", { "data-f": "hello" }), h("p", { "data-f": "wait" }), h("div", { class: "p-dots" }, h("i"), h("i"), h("i"))),
      h("div", { class: "p-section-head" }, h("h3", {}, "Who's here"), h("span", { class: "p-live", "data-f": "count" })),
      h("ul", { class: "p-players", "data-f": "players" }),
      rules ? h("details", { class: "p-rules" }, h("summary", {}, "How to play"), h("ul", {}, ...rules.map((r) => h("li", {}, r)))) : null,
    ),
  );
  const rounds = `${room.settings.rounds} round${room.settings.rounds === 1 ? "" : "s"}`;
  setField(el, "hello", me ? `You're in, ${me}!` : "You're in!");
  setField(el, "wait", `${room.gameTitle} · ${rounds}. Waiting for the host to start the game…`);
  setField(el, "count", `${room.players.length} player${room.players.length === 1 ? "" : "s"}`);
  setList(
    el,
    "players",
    ...room.players.map((p) => h("li", { class: `${p.connected ? "" : "offline"}${p.id === playerId ? " me" : ""}` }, p.nickname, p.id === playerId && h("small", {}, "you"))),
  );
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
function toast(message: string) {
  toastEl.textContent = message;
  toastEl.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (toastEl.hidden = true), 3000);
}

// ---------------------------------------------------------------- boot

const urlGame = new URLSearchParams(location.search).get("game");
const session = loadSession();
if (urlGame && session?.gameId === urlGame) {
  pendingGameId = null;
  join({ id: session.gameId, name: session.gameName }, session.pin, session.nickname); // reload → silent rejoin
} else {
  showHome();
}
